// react_frontend/src/Components/Modules/ProductSetting/ProductList.js
import React, { useEffect, useState, useMemo, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link, useNavigate } from "react-router-dom";
import { MotherContext } from "../../../Context/MotherContext";

import Swal from "sweetalert2";

// RSuite Components
import { Button, Input, Badge, Loader, Message, Modal, toaster } from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaBox,
  FaCalendarAlt,
  FaReceipt,
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrashAlt,
  FaStore,
} from "react-icons/fa";
import moment from "moment";

const PurchaseList = () => {
  const navigate = useNavigate();
  const {
    purchases,
    loadingPurchases,
    startPurchasePolling,
    stopPurchasePolling,
  } = useContext(MotherContext);

  useEffect(() => {
    startPurchasePolling();
    return () => stopPurchasePolling();
  }, [startPurchasePolling, stopPurchasePolling]);

  const [search, setSearch] = useState("");
  const [modalData, setModalData] = useState(null);

  const isAdminOrAccount = ["1", "2", "3"].includes(
    localStorage.getItem("who_am_i")
  );

  // Fetch Data
  // useEffect(() => {
  //   const fetchPurchases = async () => {
  //     setLoading(true);
  //     try {
  //       const response = await AxiosAuthInstance.get("purchases");
  //       setData(response.purchases.purchases || []);
  //     } catch (error) {
  //       console.error("Failed to load purchases", error);
  //       toaster.push(
  //         <Message type="error" duration={5000}>
  //           Failed to load purchase data.
  //         </Message>,
  //         { placement: "topEnd" }
  //       );
  //       setData([]);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchPurchases();
  // }, []);

  // Filtered Data
  const filteredData = useMemo(() => {
    return purchases.filter((item) =>
      item.receipt_no?.toLowerCase().includes(search.toLowerCase())
    );
  }, [purchases, search]);

  // Handle View
  const handleView = async (receiptNo) => {
    try {
      const response = await AxiosAuthInstance.get(`/purchases/receipt/${receiptNo}`);
      const items = response.data.purchases || [];

      if (items.length === 0) {
        toaster.push(
          <Message type="warning" duration={3000}>
            No items found for this receipt.
          </Message>,
          { placement: "topEnd" }
        );
        return;
      }

      const firstItem = items[0];

      const organization_name = firstItem.organization_name || "Unknown Supplier";
      const organization_address = firstItem.organization_address || "Address not available";
      const receipt_no = firstItem.receipt_no || "N/A";
      const purchase_date = firstItem.purchase_date || null;

      setModalData({
        purchases: items,
        organization_name,
        organization_address,
        receipt_no,
        purchase_date,
      });
    } catch (err) {
      console.error("Failed to fetch receipt:", err);
      toaster.push(
        <Message type="error" duration={5000}>
          Failed to load receipt details.
        </Message>,
        { placement: "topEnd" }
      );
    }
  };
  // Handle Delete Item
  const handleDeleteItem = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this item!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`/purchases/${item.id}`);
        setModalData((prev) => ({
          ...prev,
          purchases: prev.purchases.filter((i) => i.id !== item.id),
        }));
        toaster.push(
          <Message type="success" duration={3000}>
            Item deleted successfully!
          </Message>,
          { placement: "topEnd" }
        );
      } catch (err) {
        toaster.push(
          <Message type="error" duration={5000}>
            Failed to delete item.
          </Message>,
          { placement: "topEnd" }
        );
      }
    }
  };

  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Date",
      selector: (row) => row.purchase_date,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaCalendarAlt className="text-primary" />
          <small>{moment(row.purchase_date).format("MMM DD, YYYY")}</small>
        </div>
      ),
    },
    {
      name: "Receipt No",
      selector: (row) => row.receipt_no,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaReceipt /> {row.receipt_no}
        </Badge>
      ),
    },
    {
      name: "Total Items",
      selector: (row) => row.total_items,
      width: "120px",
      center: true,
      cell: (row) => (
        <Badge
          color="cyan"
          content={row.total_items}
          style={{ fontSize: "0.8em" }}
        />
      ),
    },
    {
      name: "Total Price",
      selector: (row) => row.total_price,
      width: "140px",
      center: true,
      cell: (row) => (
        <strong className="text-success">
          {Number(row.total_price).toLocaleString()}
        </strong>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          size="sm"
          color="yellow"
          startIcon={<FaEye />}
          onClick={() => handleView(row.receipt_no)}
          title="View receipt details"
        >
          View
        </Button>
      ),
      width: "120px",
      center: true,
    },
  ];

  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "70px",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
      },
    },
  };

  // Calculate Totals for Modal Footer
  const getTotalItems = () => modalData?.purchases?.length || 0;
  const getTotalPrice = () =>
    modalData?.purchases
      ?.reduce((sum, i) => sum + Number(i.price), 0)
      .toFixed(2) || "0.00";

  return (
    <>
      <Breadcrumb title="Purchase List" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaBox className="me-2" />
          Purchases
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
          {/* Search */}
          <Input
            placeholder="Search by receipt no..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />

          {/* Add Purchase Button */}
          {isAdminOrAccount && (
            <Link to="/add_purchase" style={{ textDecoration: "none" }}>
              <Button
                appearance="primary"
                startIcon={<FaPlus />}
                className="d-flex align-items-center custom-bg"
              >
                Add Purchase
              </Button>
            </Link>
          )}
        </div>
      </div>

      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loadingPurchases}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" />{" "}
            <span className="ms-2">Loading purchases...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaBox size={48} className="mb-3 text-secondary" />
            <h5>No purchases found</h5>
            <p>Try adding a new purchase or adjust your search.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />

      {/* View Modal */}
      <Modal open={!!modalData} onClose={() => setModalData(null)} size="lg">
        <Modal.Header>
          <Modal.Title>
            <FaStore /> {modalData?.organization_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p>
              <strong>Address:</strong>{" "}
              {modalData?.organization_address || "N/A"}
            </p>
            <p>
              <strong>Receipt No:</strong> {modalData?.receipt_no || "N/A"}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {modalData?.purchase_date
                ? moment(modalData.purchase_date).format("MMMM Do YYYY")
                : "N/A"}
            </p>
          </div>

          {modalData?.purchases?.length === 0 ? (
            <p className="text-muted">No items in this purchase.</p>
          ) : (
            <div>
              {modalData?.purchases?.map((item) => (
                <div
                  key={item.id}
                  className="border p-3 mb-3 rounded bg-light"
                  style={{ position: "relative" }}
                >
                  <h6 className="fw-medium">{item.item_name}</h6>
                  <small>
                    {item.quantity || 0} {item.product_unit || "unit"} @ {item.unit_price || "0"} ={" "}
                    <strong>{item.price}</strong>
                  </small>
                  <div className="text-muted small mt-1">
                    Created by: {item.creator_name}
                  </div>

                  <div className="mt-2 d-flex gap-2">
                    <Button
                      size="sm"
                      color="blue"
                      startIcon={<FaEdit />}
                      onClick={() => navigate(`/edit_purchase/${item.id}`)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      color="red"
                      startIcon={<FaTrashAlt />}
                      onClick={() => handleDeleteItem(item)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Badge color="blue" content={`Items: ${getTotalItems()}`}
              style={{ fontSize: "0.9em", fontWeight: "bold", padding: "5px 5px" }} />
            <Badge
              color="green"
              content={`Total: ${getTotalPrice()}`}
              style={{ fontSize: "0.9em", fontWeight: "bold", padding: "5px 5px" }}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PurchaseList;
